import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Trash2, Phone, MessageCircle, ChevronDown, CheckCircle2, XCircle, Share2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageTitle, Badge, ConfirmDelete, SelectInput } from "@/components/admin/ui";
import { useSettings } from "@/hooks/useSite";
import { formatDate, telLink, waLink } from "@/lib/format";
import { cn } from "@/lib/utils";

const STATUS_TONE = { new: "red", contacted: "gold", closed: "green" };

export const leadSummary = (l) => {
  const lines = [
    `${l.type === "test_drive" ? "🚗 Test drive request" : "✉️ Website enquiry"} — West Canal Motors`,
    `Name: ${l.name}`,
    `Phone: ${l.phone}`,
    l.email && `Email: ${l.email}`,
    l.vehicle && `Vehicle: ${l.vehicle}`,
    l.preferred_date && `Preferred: ${l.preferred_date}${l.preferred_time ? ` ${l.preferred_time}` : ""}`,
    l.message && `Message: ${l.message}`,
  ].filter(Boolean);
  return lines.join("\n");
};

export default function LeadsAdmin() {
  const qc = useQueryClient();
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [expanded, setExpanded] = useState(null);
  const { data: settings } = useSettings();
  const teamNumber = (settings?.alert_whatsapp_numbers || "").split(/[,\n;]+/).map((s) => s.trim()).filter(Boolean)[0] || settings?.whatsapp || "";

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["admin-leads", type, status],
    queryFn: async () => (await api.get("/admin/leads", { params: { ...(type && { type }), ...(status && { status }) } })).data,
    staleTime: 0,
  });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["admin-leads"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); };

  const update = useMutation({
    mutationFn: async ({ id, status }) => api.patch(`/admin/leads/${id}`, { status }),
    onSuccess: () => { toast.success("Lead updated"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const remove = useMutation({
    mutationFn: async (id) => api.delete(`/admin/leads/${id}`),
    onSuccess: () => { toast.success("Lead deleted"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  return (
    <div data-testid="admin-leads-page">
      <PageTitle eyebrow="Customers" title="Leads">
        <SelectInput value={type} onChange={(e) => setType(e.target.value)} className="!w-44 !py-2.5" data-testid="leads-filter-type">
          <option value="">All types</option><option value="test_drive">Test drives</option><option value="enquiry">Enquiries</option>
        </SelectInput>
        <SelectInput value={status} onChange={(e) => setStatus(e.target.value)} className="!w-44 !py-2.5" data-testid="leads-filter-status">
          <option value="">All statuses</option><option value="new">New</option><option value="contacted">Contacted</option><option value="closed">Closed</option>
        </SelectInput>
      </PageTitle>

      {isLoading ? <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-crimson" /></div> : (
        <div className="admin-card overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm" data-testid="admin-leads-table">
            <thead className="border-b border-white/10 font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500">
              <tr><th className="px-5 py-4 font-normal">Received</th><th className="px-5 py-4 font-normal">Type</th><th className="px-5 py-4 font-normal">Customer</th><th className="px-5 py-4 font-normal">Vehicle</th><th className="px-5 py-4 font-normal">Preferred</th><th className="px-5 py-4 font-normal">Status</th><th className="px-5 py-4 font-normal text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {leads.map((l) => (
                <LeadRow key={l.id} lead={l} teamNumber={teamNumber} expanded={expanded === l.id} onToggle={() => setExpanded(expanded === l.id ? null : l.id)} onStatus={(s) => update.mutate({ id: l.id, status: s })} onDelete={() => remove.mutate(l.id)} />
              ))}
              {!leads.length && <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-500" data-testid="leads-empty">No leads match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const LeadRow = ({ lead: l, teamNumber, expanded, onToggle, onStatus, onDelete }) => (
  <>
    <tr data-testid={`lead-row-${l.id}`} className={cn("hover:bg-white/[0.02]", expanded && "bg-white/[0.03]")}>
      <td className="px-5 py-3 font-mono text-xs text-slate-400">{formatDate(l.created_at)}</td>
      <td className="px-5 py-3"><Badge tone={l.type === "test_drive" ? "gold" : "slate"}>{l.type === "test_drive" ? "Test drive" : "Enquiry"}</Badge></td>
      <td className="px-5 py-3">
        <p className="font-display font-semibold">{l.name}</p>
        <p className="font-mono text-xs text-slate-500">{l.phone}{l.email && ` · ${l.email}`}</p>
      </td>
      <td className="px-5 py-3 text-slate-300">{l.vehicle || "—"}</td>
      <td className="px-5 py-3 text-slate-300">{l.preferred_date ? `${l.preferred_date}${l.preferred_time ? ` · ${l.preferred_time}` : ""}` : "—"}</td>
      <td className="px-5 py-3">
        <select value={l.status} onChange={(e) => onStatus(e.target.value)} data-testid={`lead-status-select-${l.id}`} className={cn("rounded-full border bg-transparent px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] outline-none", STATUS_TONE[l.status] === "red" && "border-crimson/40 text-crimson", STATUS_TONE[l.status] === "gold" && "border-gold/40 text-gold", STATUS_TONE[l.status] === "green" && "border-emerald-500/40 text-emerald-400")}>
          <option value="new">New</option><option value="contacted">Contacted</option><option value="closed">Closed</option>
        </select>
      </td>
      <td className="px-5 py-3">
        <div className="flex justify-end gap-2">
          <a href={telLink(l.phone)} title="Call" className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-slate-300 hover:border-white/40 hover:text-white"><Phone className="h-4 w-4" /></a>
          <a href={waLink(l.phone, `Hello ${l.name}, this is West Canal Motors regarding your ${l.type === "test_drive" ? "test drive request" : "enquiry"}.`)} target="_blank" rel="noreferrer" title="WhatsApp the customer" className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-slate-300 hover:border-[#25D366] hover:text-[#25D366]"><MessageCircle className="h-4 w-4" /></a>
          {teamNumber && (
            <a href={waLink(teamNumber, leadSummary(l))} target="_blank" rel="noreferrer" title="Forward lead to sales team on WhatsApp" data-testid={`lead-forward-${l.id}`} className="flex h-9 items-center gap-1.5 rounded-md border border-[#25D366]/40 px-3 font-mono text-[10px] uppercase tracking-[0.14em] text-[#25D366] hover:bg-[#25D366]/10"><Share2 className="h-3.5 w-3.5" /> Forward</a>
          )}
          <button onClick={onToggle} title="Details" data-testid={`lead-expand-${l.id}`} className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-slate-300 hover:border-white/40 hover:text-white"><ChevronDown className={cn("h-4 w-4 transition-transform", expanded && "rotate-180")} /></button>
          <ConfirmDelete title="Delete this lead?" onConfirm={onDelete} testid={`lead-delete-${l.id}`}>
            <button data-testid={`lead-delete-${l.id}`} className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-slate-300 hover:border-crimson hover:text-crimson"><Trash2 className="h-4 w-4" /></button>
          </ConfirmDelete>
        </div>
      </td>
    </tr>
    {expanded && (
      <tr className="bg-white/[0.03]" data-testid={`lead-details-${l.id}`}>
        <td colSpan={7} className="px-5 pb-5 pt-1">
          <p className="eyebrow mb-2">Message</p>
          <p className="max-w-3xl whitespace-pre-wrap text-sm text-slate-200">{l.message || "No message left."}</p>
          {l.alerts?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid={`lead-alerts-${l.id}`}>
              {l.alerts.map((a, i) => (
                <span key={i} title={a.error || a.ref || ""} className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.14em]", a.status === "sent" ? "border-emerald-500/40 text-emerald-400" : "border-crimson/40 text-crimson")}>
                  {a.status === "sent" ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />} {a.channel} alert {a.status}
                </span>
              ))}
            </div>
          )}
        </td>
      </tr>
    )}
  </>
);
